import { ref, reactive, onBeforeMount, watch } from '@vue/composition-api';
import store from '@/store';
import usePagination from '@/views/components/pagination/usePagination';
import useFilterHeaderTable from '@/views/components/filter-header-table/useFilterHeaderTable';
import useValidationModal from '@/@core/components/base-modal/hooks/useValidationModal';
import useDate from '@/utils/hooks/useDate';

import { setBannerModel } from '@/models/banner/banner';

export default function useMoveToSetBannerModal(props) {
  const fields = [
    { key: 'seq', label: 'No.' },
    { key: 'set_name', label: 'Set Name' },
    { key: 'valid_from', label: 'Valid From' },
    { key: 'valid_through', label: 'Valid Through' },
    { key: 'status', label: 'Status' },
    { key: 'actions', label: '' },
  ];

  const mappingBannerList = ref([]);
  const keyChange = ref(0);

  const loading = reactive({ tableData: false, api: true });

  const setBannerListTable = reactive({ payload: [], rows: 0 });

  const {
    fetchAssignSetBannerListModel,
    assignToSetBannerModel,
    editSetBannerModel,
  } = setBannerModel();

  const { pagination } = usePagination();
  const { resolveDateWithSeperator } = useDate();
  const { openDefaultErrorModal } = useValidationModal();
  const { filters, searchValue, onSearch } = useFilterHeaderTable();

  const onModalOpen = () => {
    pagination.currentPage = 1;

    fetchSetBannerList();
  };

  const fetchSetBannerList = (loadingAction = true) => {
    fetchAssignSetBannerListModel.payload.Banner_Id = props?.bannerId;
    fetchAssignSetBannerListModel.payload.Page = pagination.currentPage;
    fetchAssignSetBannerListModel.payload.Limit = pagination.perPage;
    fetchAssignSetBannerListModel.payload.Filters = filters;
    loading.api = true;

    if (loadingAction) {
      loading.tableData = true;
    }

    store
      .dispatch('api/apiRequest', fetchAssignSetBannerListModel)
      .then((response) => {
        setBannerListTable.payload = response.data.payload;
        setBannerListTable.rows = setBannerListTable.payload.length;
        pagination.rows = response.data.payload;
        pagination.totalRows = response.data.filtered_record;
      })
      .catch((err) => {
        openDefaultErrorModal(err);
      })
      .finally(() => {
        loading.api = false;
        loading.tableData = false;
      });
  };

  const resolveBannerStatusLabel = (status) => {
    switch (status) {
      case true:
        return 'Active';
      case false:
        return 'Inactive';

      default:
        return 'Unknown';
    }
  };

  const moveToSetBanner = (buttonProperties, setBanner) => {
    buttonProperties.disabled = true;
    buttonProperties.loading = true;
    assignToSetBannerModel.payload.Banner_Id = props?.bannerId;
    assignToSetBannerModel.payload.Set_Banner_Id = setBanner.set_banner_id;

    mappingBannerList.value = mappingBannerList.value.filter(
      (mappedBanner) => mappedBanner.banner_id !== props?.bannerId
    );

    store
      .dispatch('api/apiRequest', assignToSetBannerModel)
      .then((response) => {
        setBannerListTable.rows--;

        if (setBannerListTable.rows <= 0) {
          return pagination.currentPage--;
        }

        buttonProperties.loading = false;
        buttonProperties.disabled = false;
        fetchSetBannerList(false);
        editSetBanner(props.setBannerInformation, mappingBannerList.value);
      })
      .catch((err) => {
        openDefaultErrorModal(err);
        buttonProperties.loading = false;
        buttonProperties.disabled = false;
      })
      .finally(() => {
        keyChange.value++;
      });
  };

  const editSetBanner = (setBanner, mappingBanner) => {
    editSetBannerModel.payload.Set_Banner_Id = setBanner.set_banner_id;
    editSetBannerModel.payload.Set_Name = setBanner.set_name;
    editSetBannerModel.payload.Description = setBanner.description;
    editSetBannerModel.payload.Carousel = setBanner.carousel ? 1 : 0;
    editSetBannerModel.payload.Status = setBanner.status ? 1 : 0;
    editSetBannerModel.payload.Layout_Section = setBanner.layout_section;
    editSetBannerModel.payload.Layout_Type = setBanner.layout_type;
    editSetBannerModel.payload.Valid_From = setBanner.valid_from;
    editSetBannerModel.payload.Valid_Through = setBanner.valid_through;
    editSetBannerModel.payload.Banner_Mapping = resolveMappingBanner(
      mappingBanner
    );

    store.dispatch('api/apiRequest', editSetBannerModel);
  };

  const resolveMappingBanner = (mappingBanner) => {
    const resolvedMappingBanner = [];

    mappingBanner.map((banner, index) =>
      resolvedMappingBanner.push({
        Seq: index + 1,
        Banner_Id: banner.banner_id,
      })
    );

    return resolvedMappingBanner;
  };

  const onPageChange = (page) => {
    pagination.currentPage = page;
  };

  const onSearchSetBanner = (searchQuery) => {
    searchValue.query = searchQuery;
    onSearch();
  };

  onBeforeMount(() => {
    mappingBannerList.value = JSON.parse(JSON.stringify(props?.mappingBanner));
  });

  watch(
    () => props.mappingBanner,
    () => {
      mappingBannerList.value = JSON.parse(JSON.stringify(props.mappingBanner));
    },
    { deep: true }
  );

  watch(
    filters,
    () => {
      if (pagination.currentPage !== 1) {
        return (pagination.currentPage = 1);
      }

      fetchSetBannerList();
    },
    { deep: true }
  );

  watch(
    pagination,
    () => {
      if (!loading.api) {
        fetchSetBannerList();
      }
    },
    { deep: true }
  );

  return {
    fields,
    setBannerListTable,
    pagination,
    mappingBannerList,
    keyChange,
    loading,
    onModalOpen,
    resolveBannerStatusLabel,
    moveToSetBanner,
    resolveDateWithSeperator,
    onPageChange,
    onSearchSetBanner,
  };
}
